import {
	Tooltip as AriaTooltip,
	type TooltipProps as AriaTooltipProps,
	Button,
	Focusable,
	TooltipTrigger,
} from 'react-aria-components'
import { cn } from '../lib/cn'

/**
 * Props for the {@link Tooltip} component.
 *
 * @property children - Trigger element and tooltip content
 * @property className - Optional additional Tailwind classes for tooltip content
 *
 * @example
 * ```tsx
 * <Tooltip>
 *   <Button>Hover me</Button>
 *   <TooltipContent>Tooltip text</TooltipContent>
 * </Tooltip>
 * ```
 */
export interface TooltipProps {
	children: React.ReactNode
	className?: string
}

/**
 * Props for the {@link TooltipContent} component.
 *
 * @property children - Tooltip text content
 * @property className - Optional additional Tailwind classes
 */
export interface TooltipContentProps extends Omit<AriaTooltipProps, 'className'> {
	className?: string
}

/**
 * Accessible tooltip primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Keyboard navigation, focus management, ARIA attributes
 *   provided by React Aria's `<Tooltip>` primitive.
 * - **Styling:** All colors and spacing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Trigger:** Shows on hover and keyboard focus.
 * - **Position:** Auto-positioned by React Aria (top/bottom/left/right).
 * - **Usage:** Contextual help, icon button labels, additional information.
 *
 * @example Basic usage
 * ```tsx
 * import { Tooltip, TooltipContent } from '@atlas/ui-primitives'
 * import { IconButton } from '@atlas/ui-primitives'
 *
 * function MyComponent() {
 *   return (
 *     <Tooltip>
 *       <IconButton aria-label="Delete">
 *         <TrashIcon />
 *       </IconButton>
 *       <TooltipContent>Delete item</TooltipContent>
 *     </Tooltip>
 *   )
 * }
 * ```
 *
 * @see packages/ui-primitives/src/tooltip/tooltip.stories.tsx for visual examples
 */
export function Tooltip({ children }: TooltipProps) {
	return <TooltipTrigger delay={200}>{children}</TooltipTrigger>
}

/**
 * Tooltip content component (must be used inside Tooltip).
 *
 * @remarks
 * - Styled with the brand primary color background
 * - White text on dark background
 * - Small padding and rounded corners
 * - Drop shadow for elevation
 *
 * @example
 * ```tsx
 * <Tooltip>
 *   <Button>Hover me</Button>
 *   <TooltipContent>This is a tooltip</TooltipContent>
 * </Tooltip>
 * ```
 */
export function TooltipContent({ className, children, ...props }: TooltipContentProps) {
	return (
		<AriaTooltip
			{...props}
			offset={8}
			className={cn(
				'bg-ds-primary text-ds-primary-foreground text-ds-sm px-3 py-1.5 rounded-ds-md shadow-ds-lg',
				className,
			)}
		>
			{children}
		</AriaTooltip>
	)
}

// Re-export Button for convenience
// Bespoke-trigger bridge: RAC's TooltipTrigger attaches via FocusableProvider, so a plain element child receives nothing
export { Button as TooltipTrigger, Focusable }
